import { Injectable, OnDestroy } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { firstValueFrom, of, Subscription } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { UserPermissionsService } from '../services/user-permissions.service';
import { LoggerService } from '../../logging/logger.service';

/**
 * AuthGuard: protects routes by ensuring a valid session exists.
 * If no session is found, initiates SSO login (AzureAD) and blocks activation.
 * When a valid session exists, syncs user permissions.
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, OnDestroy {
  private inProgress = false;
  private permissionsSynced = false;
  private syncInProgress = false;
  private syncSubscription?: Subscription;

  constructor(
    private auth: AuthService,
    private userPermissionsService: UserPermissionsService,
    private router: Router,
    private logger: LoggerService,
  ) {}

  ngOnDestroy(): void {
    this.syncSubscription?.unsubscribe();
  }

  async canActivate(): Promise<boolean | UrlTree> {
    try {
      const session = await firstValueFrom(
        this.auth.amplifyGetUserSession().pipe(
          catchError((err) => {
            this.logger.error('AuthGuard session error', err);
            return of(undefined);
          }),
        ),
      );
      // Narrow expected shape (partial typing to avoid explicit any)
      type PartialSession = { tokens?: { accessToken?: unknown } } | undefined;
      const s: PartialSession = session as PartialSession;
      const hasToken = !!s?.tokens?.accessToken;
      if (hasToken) {
        // Sync user permissions on first successful authentication
        if (!this.permissionsSynced && !this.syncInProgress) {
          this.syncPermissions();
        }
        return true;
      }
    } catch (err) {
      this.logger.error('AuthGuard: exception while obtaining session', err);
    }
    if (!this.inProgress) {
      this.inProgress = true;
      this.auth.loginSSO('AzureAD');
    }
    return false;
  }
  /** Returns true when ?skip_sync=1 is present in the URL (dev/testing only). */
  private get skipSync(): boolean {
    return new URLSearchParams(window.location.search).get('skip_sync') === '1';
  }

  private syncPermissions(): void {
    this.syncInProgress = true;

    const syncStep$ = this.skipSync
      ? of({ success: true })
      : this.userPermissionsService.syncUserPermissions().pipe(
          take(1),
          catchError((err) => {
            this.logger.error('AuthGuard: Failed to sync user permissions', err);
            return of(null);
          }),
        );

    this.syncSubscription = syncStep$
      .pipe(
        switchMap((syncResponse) => {
          if (!syncResponse?.success) {
            this.userPermissionsService.permissionsLoaded.set(true);
            return of(null);
          }
          return this.userPermissionsService.getUserPermissions().pipe(
            catchError((err) => {
              this.logger.error('AuthGuard: Failed to fetch user permissions', err);
              this.userPermissionsService.permissionsLoaded.set(true);
              return of(null);
            }),
          );
        }),
      )
      .subscribe((response) => {
        this.permissionsSynced = true;
        this.syncInProgress = false;

        if (!response?.permissions) {
          return;
        }

        if (!response.permissions.global_access) {
          this.logger.warn('AuthGuard: User has no BCR roles, redirecting to access denied');
          this.userPermissionsService.applyPermissions(response.permissions);
          this.router.navigate(['/access-denied']);
          return;
        }

        this.userPermissionsService.applyPermissions(response.permissions);
      });
  }
}
